import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
    formatNumber,
    Duration,
    asRows,
    buildMemberRows,
    buildPlatformList,
    buildCountriesList,
    buildCitiesList,
    Section,
    LeaderRow,
    SourceRow,
    GeoSection,
} from "../lib/leaderboard";
import ResultsSkeleton from "../componenets/ResultsSkeleton";
import { readMetricsSnapshot, triggerRefresh } from "../lib/metricsCache";

const TrackResults = () => {
    const { track: slug } = useParams();
    const [metrics, setMetrics] = useState(null);
    const [trackConfig, setTrackConfig] = useState(null);
    const [trackMissing, setTrackMissing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                const [snapshot, trackSnap] = await Promise.all([
                    readMetricsSnapshot(),
                    getDoc(doc(db, "tracks", slug)),
                ]);
                if (cancelled) return;
                if (trackSnap.exists()) {
                    setTrackConfig({ id: trackSnap.id, ...trackSnap.data() });
                } else {
                    setTrackMissing(true);
                }

                if (snapshot && snapshot.payload) {
                    setMetrics(snapshot.payload);
                    setLoading(false);
                    if (snapshot.isStale) {
                        const fresh = await triggerRefresh();
                        if (!cancelled && fresh) setMetrics(fresh);
                    }
                    return;
                }

                const fresh = await triggerRefresh();
                if (cancelled) return;
                if (fresh) setMetrics(fresh);
                else setError({ error: "metrics_unavailable" });
            } catch (err) {
                if (!cancelled) setError({ error: "fetch_failed", detail: err.message });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [slug]);

    const termRows = asRows(metrics && metrics.metrics && metrics.metrics.utm_term);
    const comboRows = asRows(metrics && metrics.metrics && metrics.metrics.combos);

    const trackCombos = useMemo(
        () => comboRows.filter((c) => c.source === "ambasada-share" && c.campaign === slug),
        [comboRows, slug]
    );
    const totalShareViews = useMemo(() => trackCombos.reduce((s, c) => s + (c.y || 0), 0), [trackCombos]);
    const platformList = useMemo(() => buildPlatformList(trackCombos, totalShareViews), [trackCombos, totalShareViews]);
    const memberRows = useMemo(() => buildMemberRows(comboRows, termRows, slug), [comboRows, termRows, slug]);

    const namedAll = useMemo(() => memberRows.filter((m) => !m.isAnon), [memberRows]);
    const anonRow = useMemo(() => memberRows.find((m) => m.isAnon) || null, [memberRows]);
    const namedMembersCount = namedAll.length;
    const maxPlatform = Math.max(1, ...platformList.map((r) => r.value));

    const trackGeo = (metrics && metrics.byCampaign && metrics.byCampaign[slug]) || null;
    const countriesList = useMemo(
        () => buildCountriesList(trackGeo && trackGeo.country),
        [trackGeo]
    );
    const citiesList = useMemo(
        () => buildCitiesList(trackGeo && trackGeo.city),
        [trackGeo]
    );

    if (trackMissing) return <Navigate to="/" replace />;

    const trackName = trackConfig ? trackConfig.name : slug;

    return (
        <div className="bg-brand-bg min-h-screen">
            <header className="bg-white border-b border-brand-panel">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-12 lg:py-14">
                    <div className="flex items-center justify-between mb-5">
                        <Link to={`/t/${slug}`} className="text-sm text-brand-muted hover:text-brand-ink transition-colors inline-flex items-center gap-1.5">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                            Back
                        </Link>
                        <Link to="/results" className="text-sm font-semibold text-brand-ink hover:text-brand-red transition-colors">
                            Global results →
                        </Link>
                    </div>
                    <h1 className="font-brand text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-ink leading-[1.05]">
                        {trackName}<span className="text-brand-red">.</span>
                    </h1>
                    <p className="mt-4 text-base sm:text-lg text-brand-muted max-w-xl">
                        Everything below is scoped to {trackName}. <Link to="/results" className="text-brand-ink underline hover:text-brand-red">Global results</Link> aggregate across every track.
                    </p>
                    <div className="mt-5 h-1 w-24 bg-brand-red rounded-full" />
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
                {loading && <ResultsSkeleton />}

                {error && (
                    <div className="rounded-2xl bg-white border border-brand-red/30 p-6">
                        <div className="text-sm uppercase tracking-wider text-brand-red font-semibold">
                            Metrics unavailable
                        </div>
                        <div className="mt-2 text-brand-ink text-sm">
                            {error.detail || error.error || "Unknown error"}
                        </div>
                    </div>
                )}

                {!loading && !error && metrics && (
                    <>
                        <section className="rounded-3xl bg-white border border-brand-panel shadow-brand overflow-hidden">
                            <div className="p-8 sm:p-12 lg:p-14">
                                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-red">
                                    {trackName} share metrics
                                </div>

                                {(() => {
                                    const hasTime = trackGeo && trackGeo.stats && trackGeo.stats.totaltime > 0;
                                    return (
                                        <div className="mt-8 flex flex-col sm:flex-row sm:items-stretch gap-6 sm:gap-0">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-brand font-bold text-brand-ink text-[72px] sm:text-[88px] lg:text-[112px] leading-[0.9] tracking-tight">
                                                    {formatNumber(totalShareViews)}
                                                </div>
                                                <div className="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-brand-faint">
                                                    {totalShareViews === 1 ? "click" : "clicks"} delivered
                                                </div>
                                            </div>
                                            {hasTime && (
                                                <>
                                                    <div className="hidden sm:block w-px self-stretch bg-brand-panel mx-6 lg:mx-10" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-brand font-bold text-brand-ink text-[72px] sm:text-[88px] lg:text-[112px] leading-[0.9] tracking-tight">
                                                            <Duration seconds={trackGeo.stats.totaltime} />
                                                        </div>
                                                        <div className="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-brand-faint">
                                                            of prime time
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })()}

                            </div>
                        </section>

                        <Section
                            eyebrow="The leaderboard"
                            title={`Top 10 in ${trackName}`}
                            lede="Scoped to this track. Ranked by clicks. Per-member prime time lives on each member's personal page."
                        >
                            {memberRows.length === 0 ? (
                                <div className="rounded-2xl bg-white border border-brand-panel p-6 text-sm text-brand-muted">
                                    No attributed clicks yet for {trackName}. The first share in this track unlocks the leaderboard.
                                </div>
                            ) : (
                                <>
                                    <div className="rounded-2xl bg-white border border-brand-panel shadow-brand overflow-hidden divide-y divide-brand-panel">
                                        {namedAll.slice(0, 10).map((row, i) => (
                                            <LeaderRow key={row.id} row={row} rank={i + 1} />
                                        ))}
                                        {anonRow && <LeaderRow row={anonRow} rank={null} isAnon />}
                                    </div>
                                    {namedAll.length > 10 && (
                                        <div className="mt-4 text-center text-sm text-brand-muted">
                                            + {namedAll.length - 10} more not shown
                                        </div>
                                    )}
                                </>
                            )}
                        </Section>

                        <GeoSection
                            eyebrow="The places"
                            title="Where the clicks land"
                            lede={`Countries and cities for clicks attributed to ${trackName}.`}
                            countries={countriesList}
                            cities={citiesList}
                        />

                        <Section
                            eyebrow="The channels"
                            title="Where they shared"
                            lede={`Platforms members used for ${trackName}.`}
                        >
                            <div className="rounded-2xl bg-white border border-brand-panel p-6 sm:p-7 shadow-brand">
                                {platformList.length === 0 ? (
                                    <div className="text-sm text-brand-muted">No platform data yet.</div>
                                ) : (
                                    <div className="space-y-3.5">
                                        {platformList.map((row) => (
                                            <SourceRow key={row.key} row={row} max={maxPlatform} isPlatform />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Section>

                        <section className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-brand-muted">
                            <Link to="/results" className="font-medium text-brand-ink hover:text-brand-red transition-colors whitespace-nowrap">
                                Global results
                            </Link>
                            <Link to={`/t/${slug}`} className="font-medium text-brand-ink hover:text-brand-red transition-colors whitespace-nowrap">
                                Back to the {trackName} feed
                            </Link>
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default TrackResults;
